<template>
  <div class="user-page">
    <section class="user-shell">
      <div class="user-hero">
        <div class="hero-copy">
          <nav class="user-breadcrumb" aria-label="Breadcrumb">
            <span>Home</span>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">User Management</span>
          </nav>

          <p class="hero-kicker">Admin Directory</p>
          <h1>Manage administrative access</h1>
          <p class="hero-description">
            Review admin accounts, move quickly into edit flows, and keep access management
            organized with a clearer and more polished directory experience.
          </p>
        </div>

        <div class="hero-metrics">
          <article class="metric-card">
            <span class="metric-label">Admin accounts</span>
            <strong class="metric-value">{{ admins.length }}</strong>
            <span class="metric-meta">Records currently available</span>
          </article>
          <article class="metric-card">
            <span class="metric-label">Directory state</span>
            <strong class="metric-value">Active</strong>
            <span class="metric-meta">Prepared for rapid account updates</span>
          </article>
        </div>
      </div>

      <section class="toolbar">
        <div class="toolbar-copy">
          <p class="results-kicker">Administration</p>
          <h2>All user admins</h2>
        </div>

        <router-link to="/admin/add-user" class="add-admin-btn">
          <i class="fas fa-plus" aria-hidden="true"></i>
          <span>Add New Admin</span>
        </router-link>
      </section>

      <div v-if="loading" class="loading-state">
        <b-spinner />
        <p>Loading admin directory...</p>
      </div>

      <template v-else>
        <div v-if="admins.length" class="user-grid">
          <article v-for="(item, index) in admins" :key="item.id || index" class="user-card">
            <div class="card-glow"></div>

            <div class="user-card__top">
              <div>
                <span class="user-index">Admin {{ padIndex(index) }}</span>
                <h3>{{ item.organization || 'Organization not provided' }}</h3>
              </div>
              <span class="status-badge">Admin</span>
            </div>

            <div class="user-meta">
              <div class="meta-item">
                <span class="meta-label">Username</span>
                <strong>{{ getAdminUsername(item) }}</strong>
              </div>
              <div class="meta-item">
                <span class="meta-label">Email</span>
                <strong>{{ getAdminEmail(item) }}</strong>
              </div>
              <div class="meta-item">
                <span class="meta-label">Position</span>
                <strong>{{ item.position || 'Position unavailable' }}</strong>
              </div>
              <div class="meta-item">
                <span class="meta-label">Authorization</span>
                <strong>{{
                  item.authorization?.display || item.authorization?.value || 'Not set'
                }}</strong>
              </div>
            </div>

            <div class="user-actions">
              <button type="button" class="edit-btn" @click="setAdminEdit(item.id)">
                <i class="fas fa-pen" aria-hidden="true"></i>
                <span>Edit Admin</span>
              </button>
            </div>
          </article>
        </div>

        <div v-else class="empty-state">
          <h3>No admin users available</h3>
          <p>There are no admin records to display right now.</p>
        </div>
      </template>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  data() {
    return {
      loading: true,
    };
  },

  computed: {
    ...mapGetters('admin', ['getAllAdmins', 'getAdminById']),

    admins() {
      return this.getAllAdmins?.data?.results || [];
    },
  },

  methods: {
    ...mapActions('admin', ['GET_ADMINS', 'GET_ADMIN_SINGLE']),

    getAdminUsername(item) {
      return item?.user?.username || item?.username || 'Username unavailable';
    },

    getAdminEmail(item) {
      return item?.user?.email || item?.email || 'Email unavailable';
    },

    async setAdminEdit(id) {
      await this.GET_ADMIN_SINGLE(id);
      this.$router.push('/admin/edit-user');
    },

    padIndex(index) {
      return String(index + 1).padStart(2, '0');
    },
  },

  async mounted() {
    try {
      await this.GET_ADMINS();
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style scoped>
.user-page {
  min-height: 100vh;
  padding: 1.5rem 1rem 2rem;
  background: radial-gradient(circle at top left, rgba(226, 234, 229, 0.92), transparent 28%),
    radial-gradient(circle at top right, rgba(39, 77, 62, 0.12), transparent 24%),
    linear-gradient(180deg, #f5f0e8 0%, #f6f8f5 45%, #eef3ef 100%);
}

.user-shell {
  max-width: 1320px;
  margin: 0 auto;
}

.user-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(280px, 1fr);
  gap: 1.5rem;
  padding: 1.75rem;
  border: 1px solid rgba(24, 58, 43, 0.08);
  border-radius: 28px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 249, 246, 0.9)), #fff;
  box-shadow: 0 24px 60px rgba(25, 47, 36, 0.1);
}

.user-breadcrumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #6d766f;
  font-size: 0.9rem;
}

.breadcrumb-separator {
  opacity: 0.55;
}

.breadcrumb-current {
  color: #173925;
  font-weight: 600;
}

.hero-kicker,
.results-kicker {
  margin-bottom: 0.65rem;
  color: #22633e;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-copy h1 {
  max-width: 14ch;
  margin-bottom: 0.85rem;
  color: #143222;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(2rem, 4vw, 3.25rem);
  font-weight: 700;
  line-height: 1.04;
}

.hero-description {
  max-width: 62ch;
  margin-bottom: 0;
  color: #59645d;
  font-size: 1rem;
  line-height: 1.75;
}

.hero-metrics {
  display: grid;
  gap: 1rem;
}

.metric-card {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 1.1rem 1.2rem;
  border: 1px solid rgba(24, 58, 43, 0.08);
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95), rgba(245, 247, 244, 0.9));
}

.metric-label {
  color: #66746d;
  font-size: 0.88rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.metric-value {
  color: #143222;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 2rem;
  line-height: 1.1;
}

.metric-meta {
  color: #7f8a83;
  font-size: 0.92rem;
}

.toolbar {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1rem 1.1rem;
  border: 1px solid rgba(24, 58, 43, 0.08);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.84);
  backdrop-filter: blur(18px);
}

.toolbar-copy h2 {
  margin-bottom: 0;
  color: #143222;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(1.4rem, 2.4vw, 1.9rem);
  font-weight: 700;
}

.add-admin-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.88rem 1.15rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #15422a, #2b7a4e);
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  box-shadow: 0 16px 28px rgba(21, 66, 42, 0.18);
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 320px;
  margin-top: 1.5rem;
  padding: 2rem;
  border: 1px solid rgba(24, 58, 43, 0.08);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.9);
  text-align: center;
}

.loading-state p,
.empty-state p {
  margin-bottom: 0;
  color: #707d75;
}

.user-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1.5rem;
}

.user-card {
  position: relative;
  overflow: hidden;
  padding: 1.4rem;
  border: 1px solid rgba(24, 58, 43, 0.08);
  border-radius: 24px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(245, 249, 246, 0.92)), #fff;
  box-shadow: 0 20px 38px rgba(24, 39, 28, 0.08);
}

.card-glow {
  position: absolute;
  top: -72px;
  right: -20px;
  width: 210px;
  height: 210px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(44, 122, 80, 0.12), transparent 68%);
  pointer-events: none;
}

.user-card__top,
.user-meta,
.user-actions {
  position: relative;
  z-index: 1;
}

.user-card__top {
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 1rem;
}

.user-index {
  display: inline-block;
  margin-bottom: 0.45rem;
  color: #7a887f;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.user-card__top h3 {
  margin-bottom: 0;
  color: #193724;
  font-size: 1.28rem;
  font-weight: 700;
  line-height: 1.35;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.48rem 0.8rem;
  border-radius: 999px;
  background: rgba(39, 120, 74, 0.12);
  color: #21643f;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.user-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;
  margin-top: 1.1rem;
}

.meta-item {
  padding: 0.95rem 1rem;
  border: 1px solid rgba(24, 58, 43, 0.08);
  border-radius: 18px;
  background: rgba(248, 250, 247, 0.92);
}

.meta-label {
  display: block;
  margin-bottom: 0.35rem;
  color: #7a867f;
  font-size: 0.82rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.meta-item strong {
  color: #193724;
  font-size: 0.96rem;
  line-height: 1.5;
}

.user-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1.15rem;
}

.edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  border: 0;
  border-radius: 999px;
  padding: 0.8rem 1.1rem;
  background: linear-gradient(135deg, #184a2f, #2f7e52);
  color: #fff;
  font-weight: 700;
  box-shadow: 0 14px 24px rgba(24, 74, 47, 0.2);
  width: fit-content;
}

@media (max-width: 991px) {
  .user-page {
    padding-top: 0;
  }

  .user-shell {
    margin-top: 0;
  }

  .user-hero,
  .toolbar,
  .user-meta {
    grid-template-columns: 1fr;
    flex-direction: column;
    align-items: stretch;
  }

  .user-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .user-page {
    padding: 0;
  }

  .user-shell {
    margin-top: 0;
  }

  .user-hero,
  .toolbar,
  .user-card,
  .loading-state,
  .empty-state {
    border-radius: 22px;
  }

  .user-card__top,
  .user-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .add-admin-btn,
  .edit-btn {
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .user-page {
    padding-top: 0;
  }

  .user-shell {
    margin-top: 0;
  }
}
</style>
